import { enter, leave } from 'el-transition'
import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'
import hotkeys from 'hotkeys-js'

export default class extends Controller {
  static targets = [
    'filterInput',
    'filterable',
    'formInput',
    'listbox',
    'listboxFrame',
    'loadingPlaceholder',
    'option',
    'optionCheck',
    'optionGroup',
    'optionGroupLabel',
  ]

  static values = {
    selected: String,
    src: String,
    open: Boolean,
  }

  #activeOption = undefined
  #selectedOption = undefined

  connect() {
    this.hotkeysScope = `${this.element.id}-scope`
    this.visibleOptions = this.optionTargets

    if (this.selectedValue) {
      const selectedOption = this.optionTargets.
        find((el) => el.dataset.optionValue === this.selectedValue)

      if (selectedOption) {
        this.initializingSelectedOption = true
        this.selectedOption = selectedOption
      }
    }

    useClickOutside(this)

    hotkeys(
      'down,enter,escape,shift+tab,tab,up',
      (event, handler) => this.#handleKeyboardEvent(event, handler),
    )

    hotkeys.filter = (event) => {
      const { tagName } = event.target

      return (event.target.classList.contains('js-hotkeys-enabled')) ||
        !(/^(INPUT|TEXTAREA|SELECT)/.test(tagName))
    }
  }

  clickOutside() {
    this.openValue = false
  }

  filter() {
    const filterQuery = this.filterInputTarget.value.toLowerCase()

    if (this.srcValue) {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => { this.#asyncFilter(filterQuery) }, 250)
    } else {
      this.#onPageFilter(filterQuery)
    }
  }

  openValueChanged() {
    if (this.openValue) {
      this.#resetListboxWidth()

      this.previousHotkeysScope = hotkeys.getScope()
      hotkeys.setScope(this.hotkeysScope)
      this.filterInputTarget.setAttribute('aria-expanded', true)
      enter(this.listboxTarget)

      if (this.activeOption === undefined) {
        this.#assignInitialActiveOption()
      }
    } else {
      if (hotkeys.getScope() === this.hotkeysScope) {
        hotkeys.setScope(this.previousHotkeysScope)
      }
      this.#resetFilterInputValue()
      this.filterInputTarget.setAttribute('aria-expanded', false)
      leave(this.listboxTarget)
      this.#showAllOptions()
      this.activeOption = undefined
    }
  }

  resetAfterAsyncLoad() {
    this.visibleOptions = this.optionTargets

    const selectedOption = this.optionTargets.
      find((option) => option.dataset.optionValue === this.formInputTarget.value)

    if (selectedOption) {
      this.selectedOption = selectedOption
      selectedOption.setAttribute('aria-selected', true)
    }

    this.#resetListboxWidth()
    leave(this.loadingPlaceholderTarget)
    enter(this.listboxTarget)

    this.#assignInitialActiveOption()
  }

  select(event) {
    this.selectedOption = event.target
    this.openValue = false
  }

  setActiveOption(event) {
    if (this.#mousePositionChanged(event.x, event.y)) {
      this.#storeMousePosition(event.x, event.y)
      this.activeOption = event.target
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.listboxTarget.classList.contains('hidden')) {
      this.#focusSearchInput()
      this.openValue = true
    } else {
      this.openValue = false
    }
  }

  #applySelectedOptionStyling(option) {
    const optionCheck = option.querySelector(
      "[data-components--combobox-target='optionCheck']",
    )
    let { selectedStyle } = option.dataset
    selectedStyle = selectedStyle ? selectedStyle.split(' ') : []
    option.classList.add(...selectedStyle)
    if (optionCheck) enter(optionCheck)
  }

  #assignInitialActiveOption() {
    if (this.selectedOption) {
      this.activeOption = this.selectedOption
    } else {
      this.activeOption = this.visibleOptions.at(0)
    }
  }

  async #asyncFilter(filterQuery) {
    leave(this.listboxTarget)
    enter(this.loadingPlaceholderTarget)
    this.openValue = true
    this.listboxFrameTarget.src =
      `${this.srcValue}?combobox_term=${filterQuery}&selected_id=${this.formInputTarget.value}`
  }

  #ensureActiveOptionIsVisible() {
    if (!this.visibleOptions.includes(this.activeOption)) {
      this.#assignInitialActiveOption()
    }

    this.#scrollActiveOptionIntoView()
  }

  #focusSearchInput() {
    this.filterInputTarget.focus()
    const resetValueToMoveCursor = this.filterInputTarget.value
    this.filterInputTarget.value = ''
    this.filterInputTarget.value = resetValueToMoveCursor
  }

  #handleKeyboardEvent(event, handler) {
    if (document.activeElement === this.filterInputTarget) {
      switch (handler.key) {
        case 'down':
          event.preventDefault()
          this.#moveActiveOptionDown()
          break
        case 'enter':
          if (this.openValue && this.activeOption) {
            event.preventDefault()
            this.selectedOption = this.activeOption
            this.openValue = false
          }
          break
        case 'escape':
          if (this.openValue) {
            event.preventDefault()
            this.openValue = false
          }
          break
        case 'shift+tab':
        case 'tab':
          if (this.openValue && this.activeOption) {
            this.selectedOption = this.activeOption
            this.openValue = false
          }
          break
        case 'up':
          event.preventDefault()
          this.#moveActiveOptionUp()
          break
        default:
          throw Error('unexpected keyboard input')
      }
    }
  }

  #mousePositionChanged(x, y) {
    return this.mouseX === undefined || this.mouseX !== x || this.mouseY !== y
  }

  #moveActiveOptionDown() {
    this.openValue = true

    if (this.activeOption === undefined) {
      this.#assignInitialActiveOption()
    } else {
      let newIndex =
        this.#visibleOptionsIndexForOption(this.activeOption) + 1
      newIndex %= this.visibleOptions.length

      this.activeOption = this.visibleOptions.at(newIndex)
    }

    this.#scrollActiveOptionIntoView()
  }

  #moveActiveOptionUp() {
    this.openValue = true

    if (this.activeOption === undefined) {
      this.#assignInitialActiveOption()
    } else {
      const newIndex =
        this.#visibleOptionsIndexForOption(this.activeOption) - 1

      this.activeOption = this.visibleOptions.at(newIndex)
    }

    this.#scrollActiveOptionIntoView()
  }

  #onPageFilter(filterQuery) {
    this.optionGroupLabelTargets.forEach((optionGroupLabelTarget) => {
      const filterContext =
        optionGroupLabelTarget.dataset.filterContext.toLowerCase()

      if (filterQuery === '' || filterContext.includes(filterQuery)) {
        optionGroupLabelTarget.classList.remove('hidden')
      } else {
        optionGroupLabelTarget.classList.add('hidden')
      }
    })

    this.optionTargets.forEach((optionTarget) => {
      const filterContext = optionTarget.dataset.filterContext.toLowerCase()

      if (filterQuery === '' || filterContext.includes(filterQuery)) {
        optionTarget.classList.remove('hidden')

        const optionGroup = optionTarget.
          closest("[data-components--combobox-target='optionGroup']")

        if (optionGroup) {
          const optionGroupLabel = optionGroup.
            querySelector("[data-components--combobox-target='optionGroupLabel']")
          optionGroupLabel.classList.remove('hidden')
        }
      } else {
        optionTarget.classList.add('hidden')
      }
    })

    this.openValue = true

    this.visibleOptions = this.
      optionTargets.
      filter((option) => !option.classList.contains('hidden'))

    this.#ensureActiveOptionIsVisible()
  }

  #removeSelectedOptionStyling(option) {
    const optionCheck = option.querySelector(
      "[data-components--combobox-target='optionCheck']",
    )
    let { selectedStyle } = option.dataset
    selectedStyle = selectedStyle ? selectedStyle.split(' ') : []
    option.classList.remove(...selectedStyle)
    if (optionCheck) leave(optionCheck)
  }

  #resetFilterInputValue() {
    if (this.formInputTarget.value) {
      this.filterInputTarget.value =
        this.filterInputTarget.dataset.selectedLabel
    } else {
      this.filterInputTarget.value = ''
    }
  }

  #resetListboxWidth() {
    this.listboxTarget.style.width = `${this.filterInputTarget.clientWidth}px`
  }

  #scrollActiveOptionIntoView() {
    if (!this.activeOption) return

    const listboxTopMin = this.activeOption.offsetTop +
      this.activeOption.scrollHeight -
      this.listboxTarget.offsetHeight
    const listboxTopMax = this.activeOption.offsetTop - this.activeOption.scrollHeight

    if (this.listboxTarget.scrollTop < listboxTopMin) {
      this.listboxTarget.scrollTo({ top: listboxTopMin })
    } else if (this.listboxTarget.scrollTop > listboxTopMax) {
      this.listboxTarget.scrollTo({ top: listboxTopMax })
    }
  }

  #showAllOptions() {
    this.optionGroupLabelTargets.forEach((optionGroupLabelTarget) => {
      enter(optionGroupLabelTarget)
    })

    this.optionTargets.forEach((optionTarget) => {
      enter(optionTarget)
    })

    this.visibleOptions = this.optionTargets
  }

  #storeMousePosition(x, y) {
    this.mouseX = x
    this.mouseY = y
  }

  #storeOption(option) {
    if (this.formInputTarget.value !== option.dataset.optionValue) {
      this.formInputTarget.value = option.dataset.optionValue
      this.filterInputTarget.value = option.dataset.optionLabel
      this.filterInputTarget.dataset.selectedLabel = option.dataset.optionLabel

      if (this.initializingSelectedOption) {
        this.initializingSelectedOption = undefined
      } else {
        this.formInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  #visibleOptionsIndexForOption(option) {
    return this.visibleOptions.indexOf(option)
  }

  get activeOption() {
    return this.#activeOption
  }

  set activeOption(newActiveOption) {
    if (this.#activeOption) {
      const activeStyle = this.#activeOption.dataset.activeStyle.split(' ') || []
      this.#activeOption.classList.remove(...activeStyle)
    }

    if (newActiveOption) {
      const activeStyle = newActiveOption.dataset.activeStyle.split(' ') || []
      newActiveOption.classList.add(...activeStyle)
      this.filterInputTarget.setAttribute('aria-activedescendant', newActiveOption.id)
    } else {
      this.filterInputTarget.removeAttribute('aria-activedescendant')
    }

    this.#activeOption = newActiveOption
  }

  get selectedOption() {
    return this.#selectedOption
  }

  set selectedOption(newSelectedOption) {
    if (this.#selectedOption) {
      this.#removeSelectedOptionStyling(this.#selectedOption)
      this.#selectedOption.setAttribute('aria-selected', false)
    }

    if (newSelectedOption) {
      this.#applySelectedOptionStyling(newSelectedOption)
      this.#storeOption(newSelectedOption)
      newSelectedOption.setAttribute('aria-selected', true)
    }

    this.#selectedOption = newSelectedOption
  }
}
